import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowRight, Layers, Flame } from "lucide-react";
import WaitlistTile from "../components/WaitlistTile";

const UPCOMING_DROPS = [
  {
    slug: "we-have-heart-reissue",
    title: "We Have Heart",
    subtitle: "Reissue · Fixed Print",
    blurb: "The classic's coming back — same heart, fresh print. Tees, hoodies, mugs and tanks.",
    image: "/api/designs/we-have-heart-fixed-v1.png",
    featured: true,
  },
  {
    slug: "beer-thirty-somewhere",
    title: "Beer:30 Somewhere",
    subtitle: "New Drop · Bonfire Edition",
    blurb: "It's always Beer:30 somewhere in the backroads. Vintage pocket-watch artwork.",
    image: "/api/designs/beer-thirty-somewhere-v1.png",
  },
  {
    slug: "tailgate-therapy",
    title: "Tailgate Therapy",
    subtitle: "New Drop · Sunday Cure",
    blurb: "Cheaper than a shrink — pickup bed, cold cooler, sunset behind ya.",
    image: "/api/designs/tailgate-therapy-v1.png",
  },
  {
    slug: "mud-over-pavement",
    title: "Mud > Pavement",
    subtitle: "New Drop · Off-Road Only",
    blurb: "Born off-road. Built for the dirt. Tire-tread typography for the muddy faithful.",
    image: "/api/designs/mud-over-pavement-v1.png",
  },
  {
    slug: "whiskey-bent-hell-bound",
    title: "Whiskey Bent & Hell Bound",
    subtitle: "New Drop · Outlaw Country",
    blurb: "Whiskey, thorns, and a wooden cross. Pure outlaw country attitude.",
    image: "/api/designs/whiskey-bent-hell-bound-v1.png",
  },
];

/**
 * Group all products by their design (heuristic: shared base name before
 * the product-type suffix like "Tee", "Hoodie", "Mug").
 * Lets customers pick a design first, then any product type.
 */
export default function Designs() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, []);

  const designs = useMemo(() => {
    const suffixes = [
      "Tee","Hoodie","Mug","Tank","Hat","Snapback Hat","Dad Hat","Koozie",
      "Sweatshirt","Cap","Phone Case","Sticker","T-Shirt","Beanie",
    ];
    const groups = new Map();
    for (const p of products) {
      let key = p.name;
      for (const s of suffixes) {
        const re = new RegExp(`\\s*${s}(\\s|$)`, "i");
        if (re.test(key)) {
          key = key.replace(re, "").trim();
          break;
        }
      }
      key = key
        .replace(/\(Classic\)/i, "")
        .replace(/\s+\/\s+Established\s+2026/i, "")
        .replace(/Everythang Redneck/i, "")
        .replace(/^\s+|\s+$/g, "")
        .trim() || "Everythang Redneck";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    }
    return Array.from(groups.entries())
      .map(([name, items]) => ({
        name,
        items,
        cover: items[0]?.image_url,
        min_price: Math.min(...items.map((p) => p.price_from || 0)),
        product_types: items.length,
      }))
      .sort((a, b) => b.product_types - a.product_types);
  }, [products]);

  return (
    <div className="min-h-screen max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-20" data-testid="designs-page">
      <div className="mb-12">
        <div className="font-anton uppercase tracking-[0.3em] text-[#DD5422] text-sm mb-2 flex items-center gap-2">
          <Layers className="w-4 h-4" /> / Pick Your Poison
        </div>
        <h1 className="font-anton text-5xl md:text-7xl uppercase tracking-tight">Designs</h1>
        <p className="text-[#8D9688] mt-4 max-w-2xl">
          Pick a design → then choose your product. Same graphic on tees, hoodies, mugs, hats, koozies. One look, every way you want it.
        </p>
      </div>

      {loading ? (
        <div className="text-[#8D9688] uppercase tracking-widest" data-testid="designs-loading">Loadin&apos; up...</div>
      ) : (
        <>
          {/* Upcoming Drops — waitlist-driven previews */}
          <section className="mb-16" data-testid="upcoming-drops">
            <div className="flex items-center gap-3 mb-6">
              <Flame className="w-6 h-6 text-[#DD5422]" fill="#DD5422" fillOpacity={0.3} />
              <h2 className="font-anton text-3xl md:text-4xl uppercase tracking-tight">Upcoming Drops</h2>
            </div>
            <p className="text-[#8D9688] mb-6 max-w-2xl text-sm uppercase tracking-widest">
              Get on the list &mdash; first holler when each drop lands.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {UPCOMING_DROPS.map((d) => (
                <WaitlistTile key={d.slug} {...d} />
              ))}
            </div>
          </section>

          {/* Available Now */}
          <section data-testid="available-now">
            <div className="flex items-center gap-3 mb-6">
              <Layers className="w-6 h-6 text-[#DD5422]" />
              <h2 className="font-anton text-3xl md:text-4xl uppercase tracking-tight">Available Now</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {designs.map((d, i) => (
                <Link
                  key={d.name}
                  to={`/designs/${encodeURIComponent(d.name)}`}
                  data-testid={`design-${d.name}`}
                  className="group bg-[#151A14] border-2 border-[#2C332A] hover:border-[#DD5422] transition-all hover:-translate-y-1 fade-up block"
                  style={{ animationDelay: `${i * 0.04}s` }}
                >
                  <div className="relative aspect-square overflow-hidden bg-[#0A0E0B]">
                    {d.cover ? (
                      <img src={d.cover} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#2C332A] font-anton text-6xl">ER</div>
                    )}
                    <div className="absolute top-3 right-3 bg-[#DD5422] text-[#0A0E0B] px-3 py-1 font-anton text-sm uppercase tracking-widest">
                      {d.product_types} {d.product_types === 1 ? "item" : "items"}
                    </div>
                  </div>
                  <div className="p-5 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-anton text-xl uppercase tracking-tight truncate">{d.name}</div>
                      <div className="text-xs text-[#8D9688] uppercase tracking-widest mt-1">from ${d.min_price.toFixed(2)}</div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-[#DD5422] group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
